import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Clock, Play, History } from 'lucide-react'

import {
  useTriggerSchedulerTask,
  useUpdateSchedulerTask,
} from '@/hooks/useAdmin'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import type { SchedulerTask, SchedulerTaskRun } from '@/services/adminApi'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'

export const Route = createFileRoute('/admin/scheduler')({
  component: AdminSchedulerPage,
})

function statusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="default" className="bg-green-600">Completed</Badge>
    case 'running':
      return <Badge variant="default" className="bg-blue-600">Running</Badge>
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '-'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatTime(iso: string | null): string {
  if (!iso) return '-'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
}

function RunsDialog({
  task,
  isOpen,
  onClose,
}: {
  task: SchedulerTask | null
  isOpen: boolean
  onClose: () => void
}) {
  const { data, isLoading } = useQuery(adminQueries.schedulerRuns(task?.key || ''))
  const runs = data?.runs || []

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Run History: {task?.key}</DialogTitle>
          <DialogDescription>{task?.description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : runs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No runs yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run: SchedulerTaskRun) => (
                  <TableRow key={run.id}>
                    <TableCell>{statusBadge(run.status)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{run.triggerType}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatTime(run.startedAt)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDuration(run.durationMs)}
                    </TableCell>
                    <TableCell className="text-sm text-destructive max-w-[200px] truncate">
                      {run.error || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function AdminSchedulerPage() {
  const { data, isLoading } = useQuery(adminQueries.schedulerTasks())
  const triggerMutation = useTriggerSchedulerTask()
  const updateMutation = useUpdateSchedulerTask()
  const [selectedTask, setSelectedTask] = useState<SchedulerTask | null>(null)

  const tasks = data?.tasks || []

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Clock className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Scheduler</h1>
          <p className="text-muted-foreground">Scheduled tasks and run history</p>
        </div>
      </div>

      <Card flush className="overflow-hidden">
        <CardHeader>
          <CardTitle>Tasks</CardTitle>
          <CardDescription>
            {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'} registered
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <div className="rounded-md border max-sm:-mx-6 max-sm:rounded-none max-sm:border-x-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead>Last Run</TableHead>
                    <TableHead>Total Runs</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task: SchedulerTask) => (
                    <TableRow key={task.key}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{task.key}</div>
                          <div className="text-sm text-muted-foreground">{task.description}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {task.cron || 'none'}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={task.enabled}
                          onCheckedChange={(enabled) =>
                            updateMutation.mutate({ key: task.key, enabled })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {task.lastRun ? (
                          <div className="flex items-center gap-2">
                            {statusBadge(task.lastRun.status)}
                            <span className="text-sm text-muted-foreground">
                              {formatTime(task.lastRun.startedAt)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Never</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{task.totalRuns}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => triggerMutation.mutate(task.key)}
                            disabled={!task.enabled || triggerMutation.isPending}
                            title="Run now"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSelectedTask(task)}
                            title="View history"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <RunsDialog
        task={selectedTask}
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
      />
    </div>
  )
}

export default AdminSchedulerPage
